"use client";

import { useState } from "react";
import { MapPin, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useStoreLocation } from "@/context/store-location-context";
import AddressChooser, { type Address } from "@/components/checkout/address-chooser";
import DeliveryWindowChooser from "@/components/checkout/delivery-window-chooser";

type DeliveryInfoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function DeliveryInfoContent() {
  const { storeLocation, setStoreLocation } = useStoreLocation();
  const [selectedAddressId, setSelectedAddressId] = useState<string | number | null>(storeLocation.addressId);
  const [openSections, setOpenSections] = useState<string[]>(["address"]);

  const handleAddressSelect = (addressId: string | number, address: Address) => {
    setSelectedAddressId(addressId);
    setOpenSections((prev) => [...prev.filter((s) => s !== "address"), "delivery"]);
    setStoreLocation({
      city: address.city,
      microhubId: address.microhub_id ? String(address.microhub_id) : null,
      addressId,
      fullAddressStr: address.full_address,
    });
  };

  return (
    <Accordion
      type="multiple"
      value={openSections}
      onValueChange={setOpenSections}
      className="w-full"
    >
      <AccordionItem value="address" className="border-b">
        <AccordionTrigger className="hover:no-underline cursor-pointer px-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-teal-100 text-teal-600">
              <MapPin className="h-4 w-4" />
            </div>
            <span className="font-semibold">Dirección de entrega</span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <AddressChooser
            selectedAddressId={selectedAddressId}
            onSelectAddress={handleAddressSelect}
            listStyle
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="delivery" className="border-b-0">
        <AccordionTrigger className="hover:no-underline cursor-pointer px-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-teal-100 text-teal-600">
              <Calendar className="h-4 w-4" />
            </div>
            <span className="font-semibold">Ventanas de entrega</span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <DeliveryWindowChooser
            selectedAddressId={selectedAddressId}
            readOnly
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default function DeliveryInfoDialog({ open, onOpenChange }: DeliveryInfoDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden px-0">
          <DialogHeader className="flex-shrink-0 px-6">
            <DialogTitle>Siguiente entrega</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1">
            <DeliveryInfoContent />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader className="flex-shrink-0">
          <DrawerTitle>Entrega</DrawerTitle>
        </DrawerHeader>
        <div className="pb-8 overflow-y-auto flex-1">
          <DeliveryInfoContent />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
